import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle, BarChart3, PieChart, Layers } from 'lucide-react';

export const ApproachSection = () => {
  return (
    <section id="approach" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
            The <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">IndexMaster</span> Approach
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            An innovative Active Index investing Portfolio Management Service that combines the best of both active and passive investing strategies.
          </p>
        </div>

        {/* Core Concept */}
        <div className="mb-16">
          <Card className="bg-gradient-to-br from-blue-50 to-green-50 border-blue-200 shadow-xl">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl font-bold text-slate-800 mb-4">
                Core Investment Philosophy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-500 rounded-full p-2 mt-1">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-slate-800 mb-2">Active Component</h4>
                      <p className="text-slate-600">Strategic fund manager insights aligned with market perspectives and tactical asset allocation decisions.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="bg-green-500 rounded-full p-2 mt-1">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-slate-800 mb-2">Passive Component</h4>
                      <p className="text-slate-600">Systematic mirroring of market benchmarks and index-based investing to capture broad market returns.</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-600 rounded-full p-2 mt-1">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-slate-800 mb-2">Rule-Based Approach</h4>
                      <p className="text-slate-600">Systematic investment decisions on indexes and ETFs designed to minimize human bias and emotional investing.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="bg-green-600 rounded-full p-2 mt-1">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-slate-800 mb-2">Superior Risk-Adjusted Returns</h4>
                      <p className="text-slate-600">Consistent performance delivery through disciplined investment methodology and risk management.</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Investment Vehicles */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-slate-800 text-center mb-12">Diverse Investment Universe</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="hover:shadow-xl transition-all duration-300 border-blue-100">
              <CardHeader className="text-center">
                <BarChart3 className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <CardTitle className="text-lg">ETFs & Index Funds</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Index based ETFs</li>
                  <li>• Smart Beta ETFs</li>
                  <li>• Liquid Fund ETFs</li>
                  <li>• Gold & Silver ETFs</li>
                  <li>• International ETFs</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 border-green-100">
              <CardHeader className="text-center">
                <PieChart className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <CardTitle className="text-lg">Mutual Funds</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Overnight Funds</li>
                  <li>• Liquid Funds</li>
                  <li>• Debt Funds</li>
                  <li>• Ultra Short Term Funds</li>
                  <li>• Equity Arbitrage Funds</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-all duration-300 border-blue-100">
              <CardHeader className="text-center">
                <Layers className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <CardTitle className="text-lg">Listed Securities</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>• Large Cap Stocks</li>
                  <li>• Mid Cap Stocks</li>
                  <li>• Small Cap Stocks</li>
                  <li>• Sectoral ETFs</li>
                  <li>• Thematic ETFs</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="bg-slate-50 rounded-3xl p-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <h4 className="text-3xl font-bold text-blue-600 mb-2">29+</h4>
              <p className="text-slate-600">Years Experience</p>
            </div>
            <div>
              <h4 className="text-3xl font-bold text-green-600 mb-2">50:50</h4>
              <p className="text-slate-600">Nifty Hybrid Benchmark</p>
            </div>
            <div>
              <h4 className="text-3xl font-bold text-blue-600 mb-2">Long-Term</h4>
              <p className="text-slate-600">Investment Horizon</p>
            </div>
            <div>
              <h4 className="text-3xl font-bold text-green-600 mb-2">Active</h4>
              <p className="text-slate-600">Index Strategy</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};